import { DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import {
  BottomSheet,
  Illustration,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useCallback } from "react";
import { useRouteMatch } from "react-router-dom";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { type ShiftBlockWithMappedDetails } from "./api/useFetchPaginatedShiftBlocks";
import { SHIFT_BLOCK_ACTIONS } from "./utils";

interface Props {
  modalState: UseModalState;
  shifts: ShiftBlockWithMappedDetails["shifts"];
  shiftBlockId: string;
  facilityId: string;
  onConfirmRequest: () => void;
}

export function RequestToBookShiftBlockBottomSheet(props: Props) {
  const { modalState, shifts, shiftBlockId, facilityId, onConfirmRequest } = props;
  const { path: currentRouterUrl } = useRouteMatch();

  const handleCancelClicked = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.DISCARD_REQUEST_THIS_BLOCK_TAPPED,
      shiftBlockId,
      facilityId,
      totalShifts: shifts.length,
      location: currentRouterUrl,
    });
    modalState.closeModal();
  }, [currentRouterUrl, facilityId, modalState, shiftBlockId, shifts.length]);

  const handleRequestClicked = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.CONFIRM_REQUEST_THIS_BLOCK_TAPPED,
      shiftBlockId,
      facilityId,
      totalShifts: shifts.length,
      location: currentRouterUrl,
    });
    modalState.closeModal();
    onConfirmRequest();
  }, [currentRouterUrl, facilityId, modalState, onConfirmRequest, shiftBlockId, shifts.length]);

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="horizontal">
          <Button fullWidth variant="outlined" size="large" onClick={handleCancelClicked}>
            Cancel
          </Button>
          <Button fullWidth variant="contained" size="large" onClick={handleRequestClicked}>
            Request
          </Button>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={4} alignItems="center">
          <Text bold variant="h4" sx={{ textAlign: "center" }}>
            Request This Block
          </Text>
          <Illustration type="request-sent" />
          <Text align="center">
            After you request this block, we will confirm the block with the facility.
          </Text>
          <Text align="center">
            Once this block is booked, you are committed to working all shifts in the block and all
            other requests will be cancelled.
          </Text>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
